import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import CsvWorkflow from "../components/CsvWorkflow";
import DetailsHeader from "../components/DetailsHeader";
import { apiRequest } from "../utils/apiRequest";
import { showToast } from "../utils/toastOptions";
import { useUserSession } from "../utils/useUserSession";

const BASE = process.env.REACT_APP_API_BASE_URL;

const CLIENT_TEMPLATE_HEADERS = [
  "e-mail*",
  "First Name*",
  "Last Name*",
  "Company Id*",
  "Directory Maintenance",
  "Forecasting",
  "Change Management",
];

const downloadClientTemplate = () => {
  const csv = CLIENT_TEMPLATE_HEADERS.join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "daitaflow-client-import-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CLIENT_EDITABLE_COLUMNS = [
  { key: "first_name", label: "First Name", width: "16%" },
  { key: "last_name", label: "Last Name", width: "16%" },
  { key: "email", label: "Email", width: "22%" },
  { key: "company_id", label: "Company ID", width: "13%" },
  { key: "directory-maintenance", label: "Dir. Maintenance", type: "checkbox", width: "11%" },
  { key: "forecast", label: "Forecasting", type: "checkbox", width: "11%" },
  { key: "change-management", label: "Change Mgmt", type: "checkbox", width: "11%" },
];

function ClientsTable() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [showDraftErrors, setShowDraftErrors] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`${BASE}/csv/clients/list/`, { method: "GET" });
      setClients(data.clients || []);
      setEdited({});
      setSelected(new Set());
    } catch (e) {
      showToast("error", e.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const editKey = (client, field) => `${client.user_id}_${client.company_id}_${field}`;

  const toggleSub = (rowIdx, toolName) => {
    const client = clients[rowIdx];
    const key = editKey(client, toolName);
    const current = edited[key] ?? client[toolName];
    setEdited((prev) => ({ ...prev, [key]: !current }));
  };

  const getChecked = (rowIdx, toolName) => {
    const client = clients[rowIdx];
    return edited[editKey(client, toolName)] ?? client[toolName] ?? false;
  };

  const setField = (rowIdx, field, value) => {
    const client = clients[rowIdx];
    setEdited((prev) => ({ ...prev, [editKey(client, field)]: value }));
  };

  const getField = (rowIdx, field) => {
    const client = clients[rowIdx];
    const key = editKey(client, field);
    return key in edited ? edited[key] : (client[field] ?? "");
  };

  const rowKey = (c) => `${c.user_id}_${c.company_id}`;

  const addDraft = () => {
    setDrafts((prev) => [
      {
        tempId: `draft-${Date.now()}-${prev.length}`,
        first_name: "",
        last_name: "",
        email: "",
        company_id: "",
        "directory-maintenance": false,
        forecast: false,
        "change-management": false,
      },
      ...prev,
    ]);
  };

  const updateDraft = (tempId, field, value) => {
    setDrafts((prev) => prev.map((d) => (d.tempId === tempId ? { ...d, [field]: value } : d)));
  };

  const removeDraft = (tempId) => {
    setDrafts((prev) => prev.filter((d) => d.tempId !== tempId));
  };

  const draftFieldError = (draft, field) => {
    if (!showDraftErrors) return false;
    const v = String(draft[field] ?? "").trim();
    if (!v) return true;
    if (field === "email") return !EMAIL_RE.test(v);
    if (field === "company_id") return !/^\d+$/.test(v);
    return false;
  };

  const draftIsValid = (d) =>
    ["first_name", "last_name", "email", "company_id"].every((f) => {
      const v = String(d[f] ?? "").trim();
      if (!v) return false;
      if (f === "email") return EMAIL_RE.test(v);
      if (f === "company_id") return /^\d+$/.test(v);
      return true;
    });

  const toggleSelected = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = clients.length > 0 && clients.every((c) => selected.has(rowKey(c)));
  const someSelected = clients.some((c) => selected.has(rowKey(c)));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(clients.map(rowKey)));
  };

  const handleDelete = async () => {
    if (!pendingDelete?.length) return;
    setDeleting(true);
    try {
      const data = await apiRequest(`${BASE}/csv/clients/delete/`, {
        method: "POST",
        body: {
          rows: pendingDelete.map((c) => ({ user_id: c.user_id, company_id: c.company_id })),
        },
      });
      const n = data?.deleted_rows ?? pendingDelete.length;
      showToast("success", `Deleted ${n} row${n !== 1 ? "s" : ""}`);
      setPendingDelete(null);
      setSelected(new Set());
      await fetchClients();
    } catch (e) {
      showToast("error", e.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const hasChanges = Object.keys(edited).length > 0 || drafts.length > 0;

  const discardChanges = () => {
    setEdited({});
    setDrafts([]);
    setShowDraftErrors(false);
  };

  const saveDrafts = async () => {
    const data = await apiRequest(`${BASE}/csv/clients/commit/`, {
      method: "POST",
      body: {
        rows: drafts.map((d, i) => ({
          row_number: i + 1,
          email: d.email.trim(),
          first_name: d.first_name.trim(),
          last_name: d.last_name.trim(),
          company_id: d.company_id.trim(),
          "directory-maintenance": d["directory-maintenance"] ? "Y" : "N",
          forecast: d.forecast ? "Y" : "N",
          "change-management": d["change-management"] ? "Y" : "N",
        })),
      },
    });
    if (data?.failed?.length) {
      throw new Error(data.failed.map((f) => `${f.email}: ${f.message}`).join("; "));
    }
    return drafts.length;
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    if (drafts.length && !drafts.every(draftIsValid)) {
      setShowDraftErrors(true);
      showToast("error", "Fill in name, a valid email and a numeric Company ID for new rows.");
      return;
    }
    setSaving(true);
    try {
      let added = 0;
      if (drafts.length) {
        added = await saveDrafts();
        setDrafts([]);
        setShowDraftErrors(false);
      }
      if (Object.keys(edited).length === 0) {
        showToast("success", `Added ${added} user${added !== 1 ? "s" : ""}`);
        await fetchClients();
        return;
      }
      const subChanges = [];
      const nameChanges = [];
      for (const [key, val] of Object.entries(edited)) {
        const parts = key.split("_");
        const userId = Number(parts[0]);
        const companyId = Number(parts[1]);
        const field = parts.slice(2).join("_");
        if (field === "first_name" || field === "last_name") {
          let existing = nameChanges.find((n) => n.user_id === userId);
          if (!existing) {
            const client = clients.find((c) => c.user_id === userId && c.company_id === companyId);
            existing = {
              user_id: userId,
              first_name: client?.first_name || "",
              last_name: client?.last_name || "",
            };
            nameChanges.push(existing);
          }
          existing[field] = val;
        } else {
          subChanges.push({
            user_id: userId,
            company_id: companyId,
            tool_name: field,
            is_subscribed: val,
          });
        }
      }
      await apiRequest(`${BASE}/csv/clients/subscriptions/update/`, {
        method: "POST",
        body: { changes: subChanges, name_changes: nameChanges },
      });
      const total = subChanges.length + nameChanges.length;
      showToast(
        "success",
        `${added ? `Added ${added} user${added !== 1 ? "s" : ""}, u` : "U"}pdated ${total} change${total !== 1 ? "s" : ""}`
      );
      await fetchClients();
    } catch (e) {
      showToast("error", e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const selectedCount = clients.filter((c) => selected.has(rowKey(c))).length;
  const editedCount = Object.keys(edited).length + drafts.length;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {clients.length} client{clients.length !== 1 ? "s" : ""}
          </Typography>
          {editedCount > 0 && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${editedCount} unsaved change${editedCount !== 1 ? "s" : ""}`}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {selectedCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setPendingDelete(clients.filter((c) => selected.has(rowKey(c))))}
            >
              Delete ({selectedCount})
            </Button>
          )}
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={addDraft}
          >
            Add Row
          </Button>
          {hasChanges && (
            <>
              <Button size="small" color="inherit" onClick={discardChanges} disabled={saving}>
                Discard
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? null : <SaveOutlinedIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          flex: 1,
          overflow: "auto",
          borderRadius: 1.5,
          "& .MuiTableCell-root": { py: 0.5, px: 1.5, borderColor: "grey.100" },
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: "grey.50",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover .row-delete": { opacity: 1 },
        }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 44 }}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              {CLIENT_EDITABLE_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  sx={{
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    overflow: "hidden",
                    width: c.width,
                    ...(c.type === "checkbox" ? { textAlign: "center" } : {}),
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
              <TableCell sx={{ width: 48, p: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map((d) => (
              <TableRow
                key={d.tempId}
                sx={{
                  bgcolor: "rgba(46,125,50,0.06)",
                  "& td:first-of-type": { boxShadow: "inset 3px 0 0 #2e7d32" },
                }}
              >
                <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                  <Tooltip title="New row (not saved yet)">
                    <FiberNewOutlinedIcon fontSize="small" color="success" sx={{ verticalAlign: "middle" }} />
                  </Tooltip>
                </TableCell>
                {CLIENT_EDITABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={col.type === "checkbox" ? { textAlign: "center" } : undefined}
                  >
                    {col.type === "checkbox" ? (
                      <Checkbox
                        size="small"
                        color="success"
                        checked={!!d[col.key]}
                        onChange={(e) => updateDraft(d.tempId, col.key, e.target.checked)}
                      />
                    ) : (
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder={col.label}
                        value={d[col.key]}
                        error={draftFieldError(d, col.key)}
                        onChange={(e) => updateDraft(d.tempId, col.key, e.target.value)}
                        inputProps={{
                          inputMode: col.key === "company_id" ? "numeric" : undefined,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                          "& .MuiOutlinedInput-input": { py: 0.6, fontSize: "0.8125rem" },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ p: 0, textAlign: "center" }}>
                  <Tooltip title="Remove new row">
                    <IconButton size="small" onClick={() => removeDraft(d.tempId)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {clients.map((c, idx) => (
              <TableRow
                key={`${c.user_id}-${c.company_id}`}
                hover
                selected={selected.has(rowKey(c))}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.has(rowKey(c))}
                    onChange={() => toggleSelected(rowKey(c))}
                  />
                </TableCell>
                {CLIENT_EDITABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      ...(col.type === "checkbox" ? { textAlign: "center" } : {}),
                    }}
                  >
                    {col.type === "checkbox" ? (
                      <Checkbox
                        size="small"
                        checked={getChecked(idx, col.key)}
                        onChange={() => toggleSub(idx, col.key)}
                        disabled={!c.company_id}
                      />
                    ) : col.key === "email" || col.key === "company_id" ? (
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                        {c[col.key] ?? "—"}
                      </Typography>
                    ) : (
                      <TextField
                        variant="standard"
                        fullWidth
                        size="small"
                        value={getField(idx, col.key)}
                        onChange={(e) => setField(idx, col.key, e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: "0.8125rem" } }}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ p: 0, textAlign: "center" }}>
                  <Tooltip title="Delete row">
                    <IconButton
                      className="row-delete"
                      size="small"
                      color="error"
                      onClick={() => setPendingDelete([c])}
                      sx={{ opacity: 0.35, transition: "opacity 120ms" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {clients.length === 0 && drafts.length === 0 && (
              <TableRow>
                <TableCell colSpan={CLIENT_EDITABLE_COLUMNS.length + 2} sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No clients yet. Click Add Row or import a CSV.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete {pendingDelete?.length === 1 ? "this row" : `${pendingDelete?.length} rows`}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Their tool subscriptions for the listed company will be removed. Users with no
            remaining subscriptions are removed from the list.
          </Typography>
          <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: "auto" }}>
            {(pendingDelete || []).map((c) => (
              <Typography key={rowKey(c)} variant="body2">
                <strong>
                  {c.first_name} {c.last_name}
                </strong>{" "}
                · {c.email} · {c.company_id ?? "no company"}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? null : <DeleteOutlineIcon />}
          >
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default function AddUsersPage() {
  const navigate = useNavigate();
  const { user } = useUserSession();
  const isClientAdmin = useMemo(() => {
    const email = (user?.email_address || "").trim().toLowerCase();
    return [
      "dkhunt@daitaflow.ai",
      "jglover@daitaflow.ai",
      "dsinghvi@daitaflow.ai",
      "jglover@sdcpartners.ca",
    ].includes(email);
  }, [user]);

  const [clientsRefreshKey, setClientsRefreshKey] = useState(0);

  return (
    <main
      className="page dash-wrapper"
      style={{
        minHeight: "calc(100vh - 48px)",
        boxSizing: "border-box",
        marginBottom: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          boxSizing: "border-box",
        }}
      >
        <DetailsHeader title="Manage Users" onBack={() => navigate(-1)} showRefresh={false} />

        {isClientAdmin && (
          <ContentCard
            p={3}
            sx={{
              pt: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Tabs value={0} sx={{ mb: 2 }}>
              <Tab label="Daitaflow Users" />
            </Tabs>
            <Stack spacing={2}>
              <CsvWorkflow
                compact
                helperText="Required columns marked with *. Tool columns accept Y/N to subscribe."
                previewUrl={`${BASE}/csv/clients/preview/`}
                commitUrl={`${BASE}/csv/clients/commit/`}
                editableColumns={CLIENT_EDITABLE_COLUMNS}
                onDownloadTemplate={downloadClientTemplate}
                onCommitSuccess={() => setClientsRefreshKey((k) => k + 1)}
                buildCommitMessage={(data) => {
                  return (
                    `Users +${data?.created_users || 0}, ` +
                    `subscriptions +${data?.created_subscriptions || 0} new / ` +
                    `${data?.updated_subscriptions || 0} restored`
                  );
                }}
              />
              <ClientsTable key={clientsRefreshKey} />
            </Stack>
          </ContentCard>
        )}
      </Box>
    </main>
  );
}
